import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Tooltip, theme } from 'antd'
import { CalendarOutlined, CheckOutlined, DownOutlined, EditOutlined, LoadingOutlined, LockOutlined, WarningOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { VISUALLY_HIDDEN_CLASS } from '../../design-system/Field/visuallyHidden'
import { useKeyedSaveStatus } from './use-save-status'
import type { IKeyedSaveStatus, SaveStatus } from './use-save-status'

export type CellAffordance = 'pencil' | 'calendar' | 'chevron'

const AFFORDANCE_ICONS: Record<CellAffordance, React.ReactNode> = {
  pencil: <EditOutlined />,
  calendar: <CalendarOutlined />,
  chevron: <DownOutlined />,
}

interface GridEditingValue {
  editingCell: string | null
  setEditingCell: (key: string | null) => void
  save: IKeyedSaveStatus
}

const GridEditingContext = createContext<GridEditingValue | null>(null)

/**
 * One open cell for every grid inside it, and one save status per cell. The statuses live here,
 * keyed by `cellKey`, because a save can re-identify the
 * row and unmount the cell that asked; the mark follows the move through SaveError.movedTo.
 */
export function GridEditingProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [editingCell, setEditingCell] = useState<string | null>(null)
  const save = useKeyedSaveStatus()
  const value = useMemo(() => ({ editingCell, setEditingCell, save }), [editingCell, save])
  return <GridEditingContext.Provider value={value}>{children}</GridEditingContext.Provider>
}

/** `fill`: the value takes the cell and ellipsizes. `content`: a closed value takes only its text's width, so the icon sits right after it. */
export type CellFit = 'fill' | 'content'

const baseFrame: React.CSSProperties = { display: 'flex', width: '100%', height: '100%', alignItems: 'center', gap: 6 }
const FRAME: Record<CellFit, React.CSSProperties> = { fill: baseFrame, content: { ...baseFrame, justifyContent: 'flex-start' } }
const unclipped: React.CSSProperties = { minWidth: 0, overflow: 'visible', textOverflow: 'clip', whiteSpace: 'normal' }
const CONTENT: Record<CellFit, React.CSSProperties> = {
  fill: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  content: { ...unclipped, flex: '0 1 auto' },
}
// An open editor always takes the whole cell, or the date input shrinks and cuts the long date.
const EDITING_CONTENT: Record<CellFit, React.CSSProperties> = { fill: { flex: 1, minWidth: 0 }, content: { ...unclipped, flex: '1 1 auto' } }
const affordanceStyle: React.CSSProperties = { fontSize: 12, lineHeight: 1 }

export interface ICellShellProps {
  display: React.ReactNode
  fit?: CellFit
  /** The button's accessible name; only used with `onActivate`. */
  editLabel?: string
  affordance?: CellAffordance
  /** An i18n id saying why the cell cannot be edited. */
  lockReason?: string
  /** The reason shows on hovering the whole cell rather than its lock icon, for a cell whose one tooltip it is. */
  lockReasonOnCell?: boolean
  status?: SaveStatus
  errorMessage?: string
  onActivate?: () => void
  /** The editable cell's button, so focus can come back to it when its editor closes. */
  focusRef?: React.Ref<HTMLSpanElement>
}

export function CellShell(props: ICellShellProps): React.ReactElement {
  const { display, fit = 'fill', editLabel, affordance = 'pencil', lockReason, lockReasonOnCell, status = 'idle', errorMessage, onActivate, focusRef } = props
  const { token } = theme.useToken()
  const { formatMessage } = useIntl()
  const frame = FRAME[fit]
  const content = (
    <span className="vt-grid-cell-content" style={CONTENT[fit]}>
      {display}
    </span>
  )

  if (lockReason) {
    const reason = formatMessage({ id: lockReason })
    // Focusable so the reason is reachable without a pointer.
    const lock = (
      <span className="vt-grid-cell-affordance vt-grid-cell-content" tabIndex={0} aria-label={reason} style={{ ...affordanceStyle, cursor: 'default' }}>
        <LockOutlined />
      </span>
    )
    const cell = (
      <span className="vt-grid-cell-quiet" style={frame}>
        <span className="vt-grid-cell-ring" aria-hidden />
        {content}
        {lockReasonOnCell ? lock : <Tooltip title={reason}>{lock}</Tooltip>}
      </span>
    )
    return lockReasonOnCell ? <Tooltip title={reason}>{cell}</Tooltip> : cell
  }

  if (!onActivate) {
    return (
      <span className="vt-grid-cell" style={frame}>
        <span className="vt-grid-cell-ring" aria-hidden />
        {content}
      </span>
    )
  }

  const activate = () => {
    if (status !== 'saving') {
      onActivate()
    }
  }

  return (
    <span
      ref={focusRef}
      className={status === 'idle' ? 'vt-grid-cell' : `vt-grid-cell vt-grid-cell-${status}`}
      role="button"
      tabIndex={0}
      aria-label={editLabel}
      style={{ ...frame, cursor: 'pointer' }}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          activate()
        }
      }}
    >
      <span className="vt-grid-cell-ring" aria-hidden />
      {content}
      <span className="vt-grid-cell-affordance vt-grid-cell-content" style={affordanceStyle}>
        {status === 'saving' && <LoadingOutlined aria-hidden />}
        {/* Inline colours: the affordance rule steps its colour on hover, and a grey check stops meaning saved. */}
        {status === 'saved' && <CheckOutlined aria-hidden style={{ color: token.colorSuccess }} />}
        {status === 'error' && (
          <Tooltip title={errorMessage}>
            <WarningOutlined aria-hidden style={{ color: token.colorError }} />
          </Tooltip>
        )}
        {status === 'idle' && AFFORDANCE_ICONS[affordance]}
      </span>
    </span>
  )
}

export interface IEditableCellProps<TValue> {
  /** Which cell is open, grid-wide - row id plus field. */
  cellKey: string
  editable: boolean
  fit?: CellFit
  affordance: CellAffordance
  editLabel: string
  display: React.ReactNode
  /** An i18n id. Present means the edit is forbidden: a lock with this reason, no editor. */
  lockReason?: string
  lockReasonOnCell?: boolean
  /** Shown when the failure carries no message of its own. */
  errorMessage?: string
  /** False for an editor that opens in a popover of its own: the cell is not where the editing happens. */
  editingRing?: boolean
  renderEditor: (actions: { commit: (value: TValue) => void; cancel: () => void }) => React.ReactNode
  /** Resolves with the key the saved mark also goes to, when the save re-identified the row. */
  onCommit: (value: TValue) => Promise<string | void>
}

export function EditableCell<TValue>(props: IEditableCellProps<TValue>): React.ReactElement {
  const { cellKey, editable, fit = 'fill', affordance, editLabel, display, lockReason, lockReasonOnCell, errorMessage, editingRing = true, renderEditor, onCommit } = props
  const { editingCell, setEditingCell, save } = useContext(GridEditingContext) as GridEditingValue
  const editing = editable && !lockReason && editingCell === cellKey
  const shellRef = useRef<HTMLSpanElement>(null)
  const refocus = useRef(false)

  // Back to the cell once its editor is gone, unless the reader has already moved focus elsewhere.
  useEffect(() => {
    if (editing || !refocus.current) return
    refocus.current = false
    const active = document.activeElement
    if (!active || active === document.body) {
      shellRef.current?.focus()
    }
  }, [editing])

  if (lockReason) {
    return <CellShell display={display} fit={fit} lockReason={lockReason} lockReasonOnCell={lockReasonOnCell} />
  }
  if (!editable) {
    return <CellShell display={display} fit={fit} />
  }

  if (editing) {
    const cancel = () => {
      refocus.current = true
      setEditingCell(null)
    }
    const commit = (value: TValue) => {
      cancel()
      void save.runSave(cellKey, () => onCommit(value))
    }
    return (
      <span
        className={editingRing ? 'vt-grid-cell vt-grid-cell-editing' : 'vt-grid-cell'}
        style={FRAME[fit]}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            cancel()
          }
        }}
      >
        <span className="vt-grid-cell-ring" aria-hidden />
        <span className="vt-grid-cell-content" style={EDITING_CONTENT[fit]}>
          {renderEditor({ commit, cancel })}
        </span>
      </span>
    )
  }

  const status = save.statusOf(cellKey)
  const message = save.errorOf(cellKey) || errorMessage
  return (
    <>
      <CellShell
        display={display}
        fit={fit}
        editLabel={editLabel}
        affordance={affordance}
        status={status}
        errorMessage={message}
        focusRef={shellRef}
        onActivate={() => {
          if (status === 'error') {
            save.clearError(cellKey)
          }
          setEditingCell(cellKey)
        }}
      />
      {status === 'error' && (
        <span role="alert" className={VISUALLY_HIDDEN_CLASS}>
          {message}
        </span>
      )}
    </>
  )
}
